import { useMemo } from "react"
import { format, parseISO } from "date-fns"
import { CalendarClock, Package } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { useAppSelector } from "@/reducers/hooks"
import {
  findPlannedOrdersForVehicle,
  firstPickupStop,
  lastDeliveryStop,
  PlannedOrder,
} from "@/reducers/plannedOrdersSlice"
import { findProfile } from "@/reducers/profilesSliceSelectors"

const formatStopTime = (value?: string) => {
  if (!value) return null
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) return value
  return format(parsed, "dd.MM.yyyy HH:mm")
}

const StopLine = ({
  label,
  stop,
}: {
  label: string
  stop: ReturnType<typeof firstPickupStop>
}) => (
  <div className="flex items-baseline gap-2 text-sm">
    <span className="w-24 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <span className="min-w-0">
      <span className="block truncate font-medium">
        {stop?.location.formattedAddress || stop?.location.locality || "—"}
      </span>
      {stop?.scheduled_arrival_time && (
        <span className="block text-xs text-muted-foreground">
          {formatStopTime(stop.scheduled_arrival_time)}
        </span>
      )}
    </span>
  </div>
)

const PlannedOrderCard = ({ order }: { order: PlannedOrder }) => {
  const { t } = useTranslation()
  const dispatcherProfile = useAppSelector(
    findProfile(order.assignedDispatcherJid),
  )
  return (
    <div className="rounded-xl border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
          <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {order.metadata?.ref_number || t("planner.common.plannedOrder")}
          </span>
        </p>
        <Badge variant="outline" className="shrink-0">
          {t("planner.vehicleOrders.plannedBadge")}
        </Badge>
      </div>
      {order.metadata?.customer_name && (
        <p className="mb-2 truncate text-xs text-muted-foreground">
          {order.metadata.customer_name}
        </p>
      )}
      <div className="space-y-1.5">
        <StopLine
          label={t("planner.common.pickup")}
          stop={firstPickupStop(order)}
        />
        <StopLine
          label={t("planner.common.delivery")}
          stop={lastDeliveryStop(order)}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {order.metadata?.weight && <span>{order.metadata.weight}</span>}
        {order.metadata?.price_netto_eur !== undefined && (
          <span>{order.metadata.price_netto_eur} EUR</span>
        )}
        {order.assignedDispatcherJid && (
          <span>
            {t("planner.vehicleOrders.dispatcher", {
              name: dispatcherProfile?.name || order.assignedDispatcherJid,
            })}
          </span>
        )}
      </div>
    </div>
  )
}

const VehiclePlannedOrders = ({ chatJid }: { chatJid: string }) => {
  const { t } = useTranslation()
  const selector = useMemo(
    () => findPlannedOrdersForVehicle(chatJid),
    [chatJid],
  )
  const orders = useAppSelector(selector)

  const sortedOrders = useMemo(
    () =>
      [...orders].sort((left, right) => {
        const leftTime = firstPickupStop(left)?.scheduled_arrival_time ?? ""
        const rightTime = firstPickupStop(right)?.scheduled_arrival_time ?? ""
        return leftTime.localeCompare(rightTime)
      }),
    [orders],
  )

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-[var(--background-primary)] p-3">
      {sortedOrders.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <CalendarClock className="h-6 w-6" />
          <p className="text-sm">{t("planner.vehicleOrders.noOrders")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedOrders.map((order) => (
            <PlannedOrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  )
}

export const useVehiclePlannedOrdersCount = (chatJid: string): number => {
  const selector = useMemo(
    () => findPlannedOrdersForVehicle(chatJid),
    [chatJid],
  )
  return useAppSelector(selector).length
}

export default VehiclePlannedOrders
